import { z } from "zod";

// Module « crm » : dossiers commerciaux (prospects → clients), contacts, suivis,
// rendez-vous, opportunités, lignes de revenus et historique. Réservé aux
// administrateurs. Les listes qui varient d'un projet à l'autre (secteurs,
// partenaires, devise) sont des OPTIONS du module, pas des enums figés.

// =============================================================
// Options du module (blueprint.json → modules.crm.options)
// =============================================================

const optionEntrySchema = z.object({
  /** Code stocké en base (stable, sans espace). */
  value: z.string().regex(/^[a-z0-9_-]+$/, "code en minuscules, chiffres, _ ou -"),
  /** Libellé affiché. */
  label: z.string().min(1),
});
export type CrmOptionEntry = z.infer<typeof optionEntrySchema>;

export const CRM_DEFAULT_SECTORS: CrmOptionEntry[] = [
  { value: "commerce", label: "Commerce" },
  { value: "services", label: "Services" },
  { value: "restauration", label: "Restauration" },
  { value: "automobile", label: "Automobile" },
  { value: "sante", label: "Santé" },
  { value: "immobilier", label: "Immobilier" },
  { value: "autre", label: "Autre" },
];

export const CRM_DEFAULT_PARTNERS: CrmOptionEntry[] = [
  { value: "interne", label: "Interne" },
  { value: "partenaire", label: "Partenaire" },
];

export const crmOptionsSchema = z
  .object({
    /** Secteurs d'activité proposés pour un dossier. */
    sectors: z.array(optionEntrySchema).min(1).default(CRM_DEFAULT_SECTORS),
    /** Secteur d'un dossier créé sans précision ; vaut aussi « non renseigné ». */
    defaultSector: z.string().default("autre"),
    /** Qui porte le dossier commercialement. */
    partners: z.array(optionEntrySchema).min(1).default(CRM_DEFAULT_PARTNERS),
    defaultPartner: z.string().default("interne"),
    /** Devise unique des montants : les cumuls n'ont de sens qu'en devise unique. */
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/, "code ISO 4217 attendu")
      .default("CAD"),
    /** Fuseau de référence pour résoudre les dates d'un compte rendu. */
    timezone: z.string().min(1).default("America/Montreal"),
  })
  .superRefine((opts, ctx) => {
    if (!opts.sectors.some((s) => s.value === opts.defaultSector)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["defaultSector"],
        message: `« ${opts.defaultSector} » n'est pas dans la liste des secteurs`,
      });
    }
    if (!opts.partners.some((p) => p.value === opts.defaultPartner)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["defaultPartner"],
        message: `« ${opts.defaultPartner} » n'est pas dans la liste des partenaires`,
      });
    }
  });
export type CrmOptions = z.infer<typeof crmOptionsSchema>;

/** Table code → libellé, prête pour un <select> ou un affichage. */
export function optionLabels(entries: CrmOptionEntry[]): Record<string, string> {
  return Object.fromEntries(entries.map((e) => [e.value, e.label]));
}

// =============================================================
// Types
// =============================================================

/** Les timestamps arrivent en ISO via REST ou en secondes Unix — toDate() gère les deux. */
export type Timestamp = number | string;

export type CrmAccountStatus = "prospect" | "en_cours" | "active" | "en_service" | "suspendu" | "perdu";
/** Code d'un secteur, validé côté API contre les options du module. */
export type CrmSector = string;
export type CrmSource = "referral" | "outbound" | "inbound" | "partenaire" | "evenement" | "autre";
/** Code d'un partenaire, validé côté API contre les options du module. */
export type CrmPartner = string;
export type CrmTaskType = "appel" | "email" | "relance" | "reunion" | "demo" | "admin" | "autre";
export type CrmTaskStatus = "open" | "done" | "cancelled";
export type CrmPriority = "low" | "normal" | "high";
export type CrmMeetingKind = "visio" | "telephone" | "sur_place" | "autre";
export type CrmMeetingStatus = "planifie" | "tenu" | "annule" | "no_show";
export type CrmOpportunityStage = "qualification" | "proposition" | "negociation" | "gagnee" | "perdue";
export type CrmRevenueKind = "abonnement" | "installation" | "usage" | "ponctuel" | "ajustement";
export type CrmRevenueStatus = "prevu" | "facture" | "encaisse" | "annule";
export type CrmActivityType =
  | "note"
  | "status_change"
  | "field_change"
  | "task"
  | "meeting"
  | "opportunity"
  | "revenue"
  | "tenant_link"
  | "system";

export interface CrmAccount {
  id: string;
  name: string;
  status: CrmAccountStatus;
  sector: CrmSector;
  source: CrmSource;
  partner: CrmPartner;
  ownerUserId: string | null;
  /** Établissement lié (module tenants), rempli à l'activation. */
  tenantId: string | null;
  legalName: string | null;
  website: string | null;
  contactName: string | null;
  contactRole: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  addressLine: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  country: string;
  setupFeeCents: number;
  mrrCents: number;
  commissionPct: number;
  currency: string;
  contractStartAt: Timestamp | null;
  contractEndAt: Timestamp | null;
  activatedAt: Timestamp | null;
  serviceStartedAt: Timestamp | null;
  lostAt: Timestamp | null;
  lostReason: string | null;
  tagsJson: string | null;
  notes: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdByUserId: string | null;
}

export interface CrmContact {
  id: string;
  accountId: string;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
  notes: string | null;
  createdAt: Timestamp;
}

export interface CrmTask {
  id: string;
  accountId: string;
  title: string;
  type: CrmTaskType;
  status: CrmTaskStatus;
  priority: CrmPriority;
  dueAt: Timestamp | null;
  completedAt: Timestamp | null;
  assignedUserId: string | null;
  notes: string | null;
  createdAt: Timestamp;
  createdByUserId: string | null;
}

export interface CrmMeeting {
  id: string;
  accountId: string;
  title: string;
  kind: CrmMeetingKind;
  startAt: Timestamp;
  endAt: Timestamp | null;
  location: string | null;
  attendees: string | null;
  status: CrmMeetingStatus;
  outcome: string | null;
  assignedUserId: string | null;
  createdAt: Timestamp;
}

export interface CrmOpportunity {
  id: string;
  accountId: string;
  title: string;
  stage: CrmOpportunityStage;
  setupCents: number;
  mrrCents: number;
  probability: number;
  expectedCloseAt: Timestamp | null;
  closedAt: Timestamp | null;
  lostReason: string | null;
  ownerUserId: string | null;
  notes: string | null;
  createdAt: Timestamp;
}

export interface CrmRevenue {
  id: string;
  accountId: string;
  kind: CrmRevenueKind;
  label: string;
  periodStart: Timestamp;
  periodEnd: Timestamp | null;
  amountCents: number;
  currency: string;
  commissionPct: number | null;
  commissionCents: number;
  status: CrmRevenueStatus;
  invoiceRef: string | null;
  paidAt: Timestamp | null;
  notes: string | null;
  createdAt: Timestamp;
}

export interface CrmActivity {
  id: string;
  accountId: string;
  type: CrmActivityType;
  body: string;
  metaJson: string | null;
  authorUserId: string | null;
  createdAt: Timestamp;
}

export interface CrmNextAction {
  id: string;
  title: string;
  dueAt: Timestamp | null;
  overdue: boolean;
}

export interface CrmAccountListItem extends CrmAccount {
  ownerName: string | null;
  tenantName: string | null;
  openTaskCount: number;
  nextAction: CrmNextAction | null;
  /** Somme des lignes encaissées, en cents. */
  collectedCents: number;
  /** Commission due sur les lignes encaissées, en cents. */
  commissionCents: number;
}

export interface CrmAccountStats {
  collectedCents: number;
  invoicedCents: number;
  forecastCents: number;
  commissionCents: number;
  openTaskCount: number;
  overdueTaskCount: number;
  nextMeetingAt: Timestamp | null;
  openOpportunityCents: number;
}

export interface CrmAccountDetail {
  account: CrmAccount;
  ownerName: string | null;
  tenantName: string | null;
  contacts: CrmContact[];
  tasks: CrmTask[];
  meetings: CrmMeeting[];
  opportunities: CrmOpportunity[];
  revenues: CrmRevenue[];
  activities: CrmActivity[];
  stats: CrmAccountStats;
}

export interface CrmPipelineStats {
  /** Nombre de dossiers par statut, tous les statuts présents (0 inclus). */
  byStatus: Record<CrmAccountStatus, number>;
  /** MRR contractuel cumulé des dossiers active + en_service, en cents. */
  activeMrrCents: number;
  /** Encaissé sur le mois courant, en cents. */
  collectedThisMonthCents: number;
  /** Commission due sur le mois courant, en cents. */
  commissionThisMonthCents: number;
  /** Valeur pondérée du pipeline ouvert (setup + 12x MRR) x probabilité, en cents. */
  weightedPipelineCents: number;
  overdueTaskCount: number;
  upcomingMeetingCount: number;
  activeAccountCount: number;
}

export interface CrmAgendaTask extends CrmTask {
  accountName: string;
}

export interface CrmAgendaMeeting extends CrmMeeting {
  accountName: string;
}

export interface CrmAgenda {
  tasks: CrmAgendaTask[];
  meetings: CrmAgendaMeeting[];
}

export interface CrmRevenueRow extends CrmRevenue {
  accountName: string;
  accountPartner: CrmPartner;
}

export interface CrmRevenueReport {
  rows: CrmRevenueRow[];
  /** Cumuls hors lignes annulées : une ligne annulée n'est pas du revenu. */
  totals: {
    amountCents: number;
    commissionCents: number;
    netCents: number;
    /** Nombre de lignes retenues dans les cumuls, annulées exclues. */
    retainedCount: number;
  };
  byStatus: Record<CrmRevenueStatus, number>;
  byMonth: Array<{ month: string; amountCents: number; commissionCents: number }>;
}

// Extraction assistée depuis un compte rendu collé. Le modèle PROPOSE, il n'écrit
// jamais : chaque valeur remonte avec sa citation et son niveau de confiance pour
// que l'utilisateur puisse trancher champ par champ.
export type CrmExtractionConfidence = "haute" | "moyenne" | "faible";

export interface CrmExtractedField {
  /** Clé d'un champ de CrmAccount. */
  key: string;
  /** Libellé français prêt à afficher. */
  label: string;
  /** Valeur à écrire (cents pour les montants, chaîne sinon). */
  value: string | number | null;
  /** Valeur formatée pour l'affichage. */
  display: string;
  /** Extrait verbatim du compte rendu qui justifie la valeur. */
  evidence: string | null;
  confidence: CrmExtractionConfidence;
  /** Valeur actuelle formatée, si le champ est déjà rempli. */
  currentDisplay: string | null;
  /** Le champ est déjà rempli avec une valeur différente. */
  conflict: boolean;
}

export interface CrmExtractedTask {
  title: string;
  type: CrmTaskType;
  dueAt: Timestamp | null;
  evidence: string | null;
  confidence: CrmExtractionConfidence;
}

export interface CrmExtractedContact {
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  evidence: string | null;
  /** Une personne du même nom ou du même courriel est déjà rattachée au dossier. */
  conflict: boolean;
}

export interface CrmExtraction {
  fields: CrmExtractedField[];
  tasks: CrmExtractedTask[];
  contacts: CrmExtractedContact[];
  summary: string | null;
  /** Suivi de coût, affiché discrètement dans l'interface. */
  usage: { inputTokens: number; outputTokens: number };
}

export interface CrmExtractionApplied {
  account: CrmAccount;
  appliedFields: number;
  createdTasks: number;
  createdContacts: number;
  /** Champs refusés par la validation : le reste de l'import a bien été écrit. */
  rejectedFields: Array<{ key: string; label: string }>;
}

// =============================================================
// Schémas zod
// =============================================================

export const crmAccountStatusSchema = z.enum([
  "prospect",
  "en_cours",
  "active",
  "en_service",
  "suspendu",
  "perdu",
]);
export const CRM_STATUS_ORDER: CrmAccountStatus[] = crmAccountStatusSchema.options;

// Secteur et partenaire : une chaîne au format d'un code d'option. L'appartenance
// à la liste configurée se vérifie côté API (400 sinon), pas ici.
export const crmOptionCodeSchema = z.string().regex(/^[a-z0-9_-]+$/);
export const crmSourceSchema = z.enum([
  "referral",
  "outbound",
  "inbound",
  "partenaire",
  "evenement",
  "autre",
]);
export const crmTaskTypeSchema = z.enum([
  "appel",
  "email",
  "relance",
  "reunion",
  "demo",
  "admin",
  "autre",
]);
export const crmTaskStatusSchema = z.enum(["open", "done", "cancelled"]);
export const crmPrioritySchema = z.enum(["low", "normal", "high"]);
export const crmMeetingKindSchema = z.enum(["visio", "telephone", "sur_place", "autre"]);
export const crmMeetingStatusSchema = z.enum(["planifie", "tenu", "annule", "no_show"]);
export const crmOpportunityStageSchema = z.enum([
  "qualification",
  "proposition",
  "negociation",
  "gagnee",
  "perdue",
]);
export const crmRevenueKindSchema = z.enum([
  "abonnement",
  "installation",
  "usage",
  "ponctuel",
  "ajustement",
]);
export const crmRevenueStatusSchema = z.enum(["prevu", "facture", "encaisse", "annule"]);
// Code ISO 4217 ; l'API n'accepte que la devise configurée dans les options.
export const crmCurrencySchema = z.string().regex(/^[A-Z]{3}$/);
export const crmActivityTypeSchema = z.enum([
  "note",
  "status_change",
  "field_change",
  "task",
  "meeting",
  "opportunity",
  "revenue",
  "tenant_link",
  "system",
]);

// Un champ vidé par un formulaire arrive en "" ou en null selon le contrôle :
// on accepte les deux plutôt que d'imposer une normalisation à chaque page.
const optionalText = z.string().nullable().optional();
const optionalEmail = z.string().email().or(z.literal("")).nullable().optional();
// Les dates transitent en ISO ; la conversion en Date se fait côté route.
const optionalDate = z.string().datetime({ offset: true }).nullable().optional();
const requiredDate = z.string().datetime({ offset: true });
const cents = z.number().int().min(0);
const pct = z.number().min(0).max(100);

export const createCrmAccountSchema = z.object({
  name: z.string().min(1),
  status: crmAccountStatusSchema.default("prospect"),
  // Absents : l'API applique le secteur / partenaire par défaut des options.
  sector: crmOptionCodeSchema.optional(),
  source: crmSourceSchema.default("autre"),
  partner: crmOptionCodeSchema.optional(),
  ownerUserId: z.string().nullable().optional(),
  tenantId: z.string().nullable().optional(),
  legalName: optionalText,
  website: optionalText,
  contactName: optionalText,
  contactRole: optionalText,
  contactEmail: optionalEmail,
  contactPhone: optionalText,
  addressLine: optionalText,
  city: optionalText,
  province: optionalText,
  postalCode: optionalText,
  country: z.string().default("CA"),
  setupFeeCents: cents.default(0),
  mrrCents: cents.default(0),
  commissionPct: pct.default(0),
  currency: crmCurrencySchema.optional(),
  contractStartAt: optionalDate,
  contractEndAt: optionalDate,
  activatedAt: optionalDate,
  serviceStartedAt: optionalDate,
  lostAt: optionalDate,
  lostReason: optionalText,
  tags: z.array(z.string()).optional(),
  notes: optionalText,
});
export type CreateCrmAccountInput = z.infer<typeof createCrmAccountSchema>;

export const updateCrmAccountSchema = createCrmAccountSchema.partial();
export type UpdateCrmAccountInput = z.infer<typeof updateCrmAccountSchema>;

export const createCrmContactSchema = z.object({
  name: z.string().min(1),
  role: optionalText,
  email: optionalEmail,
  phone: optionalText,
  isPrimary: z.boolean().default(false),
  notes: optionalText,
});
export type CreateCrmContactInput = z.infer<typeof createCrmContactSchema>;

export const updateCrmContactSchema = createCrmContactSchema.partial();
export type UpdateCrmContactInput = z.infer<typeof updateCrmContactSchema>;

export const createCrmTaskSchema = z.object({
  title: z.string().min(1),
  type: crmTaskTypeSchema.default("autre"),
  status: crmTaskStatusSchema.default("open"),
  priority: crmPrioritySchema.default("normal"),
  dueAt: optionalDate,
  completedAt: optionalDate,
  assignedUserId: z.string().nullable().optional(),
  notes: optionalText,
});
export type CreateCrmTaskInput = z.infer<typeof createCrmTaskSchema>;

export const updateCrmTaskSchema = createCrmTaskSchema.partial();
export type UpdateCrmTaskInput = z.infer<typeof updateCrmTaskSchema>;

export const createCrmMeetingSchema = z.object({
  title: z.string().min(1),
  kind: crmMeetingKindSchema.default("visio"),
  startAt: requiredDate,
  endAt: optionalDate,
  location: optionalText,
  attendees: optionalText,
  status: crmMeetingStatusSchema.default("planifie"),
  outcome: optionalText,
  assignedUserId: z.string().nullable().optional(),
});
export type CreateCrmMeetingInput = z.infer<typeof createCrmMeetingSchema>;

export const updateCrmMeetingSchema = createCrmMeetingSchema.partial();
export type UpdateCrmMeetingInput = z.infer<typeof updateCrmMeetingSchema>;

export const createCrmOpportunitySchema = z.object({
  title: z.string().min(1),
  stage: crmOpportunityStageSchema.default("qualification"),
  setupCents: cents.default(0),
  mrrCents: cents.default(0),
  probability: z.number().int().min(0).max(100).default(50),
  expectedCloseAt: optionalDate,
  closedAt: optionalDate,
  lostReason: optionalText,
  ownerUserId: z.string().nullable().optional(),
  notes: optionalText,
});
export type CreateCrmOpportunityInput = z.infer<typeof createCrmOpportunitySchema>;

export const updateCrmOpportunitySchema = createCrmOpportunitySchema.partial();
export type UpdateCrmOpportunityInput = z.infer<typeof updateCrmOpportunitySchema>;

export const createCrmRevenueSchema = z.object({
  kind: crmRevenueKindSchema.default("abonnement"),
  label: z.string().min(1),
  periodStart: requiredDate,
  periodEnd: optionalDate,
  // Négatif autorisé : une ligne « ajustement » corrige un encaissement passé.
  amountCents: z.number().int().default(0),
  // Absente : devise du dossier.
  currency: crmCurrencySchema.optional(),
  // Null -> repli sur le taux du dossier ; la commission est recalculée côté route.
  commissionPct: pct.nullable().optional(),
  status: crmRevenueStatusSchema.default("prevu"),
  invoiceRef: optionalText,
  paidAt: optionalDate,
  notes: optionalText,
});
export type CreateCrmRevenueInput = z.infer<typeof createCrmRevenueSchema>;

export const updateCrmRevenueSchema = createCrmRevenueSchema.partial();
export type UpdateCrmRevenueInput = z.infer<typeof updateCrmRevenueSchema>;

export const createCrmNoteSchema = z.object({
  body: z.string().min(1),
});
export type CreateCrmNoteInput = z.infer<typeof createCrmNoteSchema>;

// Activation d'un dossier = liaison à un établissement du module tenants, existant
// ou créé au passage. z.discriminatedUnion n'accepte pas .extend() : on duplique
// activatedAt dans chaque branche.
export const activateCrmAccountSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("link"),
    tenantId: z.string().min(1),
    activatedAt: z.string().datetime({ offset: true }).optional(),
  }),
  z.object({
    mode: z.literal("create"),
    tenantName: z.string().min(1).max(120),
    activatedAt: z.string().datetime({ offset: true }).optional(),
  }),
]);
export type ActivateCrmAccountInput = z.infer<typeof activateCrmAccountSchema>;

// Le minimum en deçà duquel un texte ne contient rien d'exploitable, et le plafond
// au-delà duquel on refuse plutôt que d'envoyer une transcription entière au modèle.
export const CRM_TRANSCRIPT_MIN_LENGTH = 20;
export const CRM_TRANSCRIPT_MAX_LENGTH = 50_000;
export const extractCrmTranscriptSchema = z.object({
  text: z.string().min(CRM_TRANSCRIPT_MIN_LENGTH).max(CRM_TRANSCRIPT_MAX_LENGTH),
});
export type ExtractCrmTranscriptInput = z.infer<typeof extractCrmTranscriptSchema>;

// L'application est un second appel explicite : l'utilisateur renvoie SEULEMENT ce
// qu'il a coché. Les trois listes sont donc indépendantes et peuvent être vides.
export const applyCrmExtractionSchema = z.object({
  /** Texte source, conservé dans l'historique du dossier pour justifier les écritures. */
  transcript: z.string().min(1),
  /** Résumé produit par l'analyse, archivé avec le compte rendu. */
  summary: z.string().nullable().optional(),
  fields: z
    .array(
      z.object({
        key: z.string().min(1),
        value: z.union([z.string(), z.number(), z.null()]),
      }),
    )
    .default([]),
  tasks: z
    .array(
      z.object({
        title: z.string().min(1),
        type: crmTaskTypeSchema.default("autre"),
        dueAt: optionalDate,
      }),
    )
    .default([]),
  contacts: z
    .array(
      z.object({
        name: z.string().min(1),
        role: optionalText,
        email: optionalEmail,
        phone: optionalText,
      }),
    )
    .default([]),
});
export type ApplyCrmExtractionInput = z.infer<typeof applyCrmExtractionSchema>;

// =============================================================
// Libellés français des enums figés (partagés API / web)
// =============================================================

export const CRM_STATUS_LABELS: Record<CrmAccountStatus, string> = {
  prospect: "Prospect",
  en_cours: "En cours",
  active: "Activé",
  en_service: "En service",
  suspendu: "Suspendu",
  perdu: "Perdu",
};

export const CRM_SOURCE_LABELS: Record<CrmSource, string> = {
  referral: "Recommandation",
  outbound: "Prospection",
  inbound: "Entrant",
  partenaire: "Partenaire",
  evenement: "Événement",
  autre: "Autre",
};

export const CRM_TASK_TYPE_LABELS: Record<CrmTaskType, string> = {
  appel: "Appel",
  email: "Courriel",
  relance: "Relance",
  reunion: "Réunion",
  demo: "Démo",
  admin: "Administratif",
  autre: "Autre",
};

export const CRM_TASK_STATUS_LABELS: Record<CrmTaskStatus, string> = {
  open: "À faire",
  done: "Terminé",
  cancelled: "Annulé",
};

export const CRM_PRIORITY_LABELS: Record<CrmPriority, string> = {
  low: "Basse",
  normal: "Normale",
  high: "Haute",
};

export const CRM_MEETING_KIND_LABELS: Record<CrmMeetingKind, string> = {
  visio: "Visioconférence",
  telephone: "Téléphone",
  sur_place: "Sur place",
  autre: "Autre",
};

export const CRM_MEETING_STATUS_LABELS: Record<CrmMeetingStatus, string> = {
  planifie: "Planifié",
  tenu: "Tenu",
  annule: "Annulé",
  no_show: "Absence",
};

export const CRM_STAGE_LABELS: Record<CrmOpportunityStage, string> = {
  qualification: "Qualification",
  proposition: "Proposition",
  negociation: "Négociation",
  gagnee: "Gagnée",
  perdue: "Perdue",
};

export const CRM_REVENUE_KIND_LABELS: Record<CrmRevenueKind, string> = {
  abonnement: "Abonnement",
  installation: "Installation",
  usage: "Usage",
  ponctuel: "Ponctuel",
  ajustement: "Ajustement",
};

export const CRM_REVENUE_STATUS_LABELS: Record<CrmRevenueStatus, string> = {
  prevu: "Prévu",
  facture: "Facturé",
  encaisse: "Encaissé",
  annule: "Annulé",
};

export const CRM_ACTIVITY_LABELS: Record<CrmActivityType, string> = {
  note: "Note",
  status_change: "Changement de statut",
  field_change: "Modification",
  task: "Suivi",
  meeting: "Rendez-vous",
  opportunity: "Opportunité",
  revenue: "Revenu",
  tenant_link: "Liaison à un établissement",
  system: "Système",
};
